import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/Services.css';

// Types for hormone therapy services
interface HormoneService {
  id: number;
  title: string;
  description: string;
  icon: string;
  features: string[];
  learnMoreLink: string;
}

// Types for process steps
interface ProcessStep {
  id: number;
  title: string;
  description: string;
}

// Types for FAQ items
interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

// Types for FAQ categories
interface FAQCategory {
  id: string;
  label: string;
}

// Types for navigation tabs
interface NavTab {
  id: string;
  label: string;
}

const Services: React.FC = () => {
  const navigate = useNavigate();
  
  // Reference for sections
  const servicesRef = useRef<HTMLDivElement>(null);
  const processRef = useRef<HTMLDivElement>(null);
  const faqRef = useRef<HTMLDivElement>(null);
  
  // FAQ data
  const faqItems: FAQItem[] = [
    {
      id: 1,
      question: "What makes bioidentical hormones different from conventional hormone therapy?",
      answer: "Bioidentical hormones are structurally identical to the hormones naturally produced by your body. Unlike synthetic hormones, bioidentical hormones match your body's natural hormones exactly, potentially reducing side effects and improving treatment outcomes. Our approach focuses on personalized dosing based on your specific hormonal needs, rather than a one-size-fits-all approach. This helps us achieve optimal hormone balance while minimizing potential risks.",
      category: "therapy"
    },
    {
      id: 2,
      question: "How long does it take to see results from hormone therapy?",
      answer: "Many women begin to notice improvements in certain symptoms like hot flashes, night sweats, and mood swings within 2-3 weeks of starting therapy. However, other benefits such as improved bone density, enhanced skin elasticity, and cognitive improvements may take 3-6 months to fully develop as your body adjusts to restored hormone levels. Each woman's experience is unique, and we track your progress closely through regular follow-ups to ensure optimal results and make any necessary adjustments to your treatment plan.",
      category: "therapy"
    },
    {
      id: 3,
      question: "Is bioidentical hormone therapy safe for long-term use?",
      answer: "When properly prescribed and monitored by qualified healthcare providers like our team at VitalBalanceHormones, bioidentical hormone therapy is considered safe for most women for long-term use. Our approach includes regular comprehensive testing and careful monitoring to ensure optimal safety and results. We consider your complete health history, family history, and risk factors when designing your treatment plan to maximize benefits while minimizing any potential risks. The key to safety lies in personalization, proper dosing, and ongoing professional oversight.",
      category: "safety"
    },
    {
      id: 4,
      question: "How do I know if I need hormone therapy?",
      answer: "If you're experiencing symptoms such as fatigue, mood changes, sleep disturbances, hot flashes, night sweats, brain fog, weight changes, or decreased libido, you may benefit from hormone therapy. However, the only way to know for certain is through proper testing. At VitalBalanceHormones, we conduct comprehensive hormone panels that go beyond standard testing to determine if imbalances are contributing to your symptoms. During your initial consultation, we'll review your symptoms, medical history, and lifestyle factors before recommending appropriate testing to guide our treatment decisions.",
      category: "therapy"
    },
    {
      id: 5,
      question: "Will insurance cover bioidentical hormone therapy?",
      answer: "Coverage for bioidentical hormone therapy varies by insurance provider and specific plan. Some insurance plans cover portions of the treatment, particularly the consultation and testing components. Custom-compounded bioidentical hormones may have different coverage than FDA-approved bioidentical formulations. Our team works with you to determine potential coverage options and provides clear information about costs. We also offer various payment plans and options to help make treatment accessible, regardless of insurance coverage.",
      category: "practical"
    },
    {
      id: 6,
      question: "What happens during the initial consultation?",
      answer: "Your initial consultation is comprehensive and typically lasts about 60 minutes. We'll review your detailed health history, current symptoms, previous treatments, lifestyle factors, and wellness goals. We explain our approach to bioidentical hormone therapy and answer any questions you have. Based on this discussion, we'll recommend appropriate testing to assess your hormone levels and other relevant biomarkers. This initial consultation is designed to be educational and collaborative, giving you the information you need to make informed decisions about your health without any pressure.",
      category: "process"
    },
    {
      id: 7,
      question: "What types of hormone testing do you use?",
      answer: "We utilize comprehensive hormone testing that goes beyond standard blood tests. Depending on your specific situation, we may recommend blood, saliva, or urine testing. Our specialized hormone panels measure not just the major hormones like estrogen, progesterone, and testosterone, but also their metabolites and related markers that provide a complete picture of your hormonal health. This thorough approach helps us identify subtle imbalances that might be missed with conventional testing, allowing for more precise treatment planning.",
      category: "process"
    },
    {
      id: 8,
      question: "Can bioidentical hormone therapy help with weight management?",
      answer: "Yes, hormone balance plays a significant role in metabolism and weight regulation. Many women experience weight gain, especially around the abdomen, when hormone levels decline. By restoring optimal hormone balance, we often see improvements in metabolic function, energy levels, and body composition. While bioidentical hormone therapy isn't primarily a weight loss treatment, many women find that when combined with appropriate nutrition and exercise, it helps them achieve and maintain a healthy weight more effectively than lifestyle changes alone.",
      category: "therapy"
    },
    {
      id: 9,
      question: "Are there any side effects with bioidentical hormone therapy?",
      answer: "Most women tolerate bioidentical hormone therapy very well, especially when properly dosed and monitored. Some may experience mild, temporary side effects as the body adjusts to treatment, such as breast tenderness, spotting, or mild fluid retention. These typically resolve within the first few weeks as your body adapts and we fine-tune your dosage. Our careful approach to dosing, starting at lower levels and gradually adjusting based on your response, helps minimize any potential side effects while maximizing benefits.",
      category: "safety"
    },
    {
      id: 10,
      question: "How often will I need follow-up appointments?",
      answer: "After your initial consultation and treatment plan development, we typically schedule a follow-up appointment 4-6 weeks after starting therapy to assess your response and make any necessary adjustments. Once your treatment is optimized, follow-up appointments are generally recommended every 3-6 months, including periodic hormone level testing to ensure continued optimal results. These regular check-ins allow us to fine-tune your treatment as your body changes and respond promptly to any new symptoms or concerns that may arise.",
      category: "process"
    },
    {
      id: 11,
      question: "Can I combine bioidentical hormone therapy with other treatments?",
      answer: "Yes, bioidentical hormone therapy often works synergistically with other wellness treatments. At VitalBalanceHormones, we take an integrative approach, frequently combining hormone therapy with nutritional guidance, targeted supplements, stress management techniques, and exercise recommendations for optimal results. We can also coordinate with your other healthcare providers to ensure all your treatments work together harmoniously. Our goal is to create a comprehensive wellness plan that addresses all aspects of your health.",
      category: "therapy"
    },
    {
      id: 12,
      question: "Do you offer virtual consultations?",
      answer: "Yes, we offer secure, HIPAA-compliant virtual consultations for both initial appointments and follow-up visits. This service makes our specialized care accessible regardless of your location. For hormone testing, we can arrange for local lab work or provide home testing kits when appropriate. While some aspects of care may require in-person visits, many women are able to receive high-quality, personalized hormone therapy primarily through our telehealth services.",
      category: "practical"
    }
  ];
  
  // Services schema for rich results
  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "about": {
      "@type": "MedicalTherapy",
      "name": "Bioidentical Hormone Replacement Therapy",
      "description": "Personalized bioidentical hormone treatments designed to address hormone imbalances in women experiencing menopause, perimenopause, and other hormonal conditions."
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://vitalbalancehormones.com/services"
    },
    "headline": "Bioidentical Hormone Therapy Services | VitalBalanceHormones",
    "specialty": {
      "@type": "MedicalSpecialty",
      "name": "Hormone Therapy"
    },
    "medicalAudience": {
      "@type": "MedicalAudience",
      "audienceType": "Women experiencing hormonal symptoms"
    }
  };
  
  // FAQPage schema for rich results in search
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  // State for active section
  const [activeTab, setActiveTab] = useState('services');
  
  // State for FAQ items
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Navigation tabs data
  const navTabs: NavTab[] = [
    {
      id: 'services',
      label: 'Our Services'
    },
    {
      id: 'process',
      label: 'Our Process'
    },
    {
      id: 'faq',
      label: 'Common Questions'
    }
  ];
  
  // FAQ categories
  const faqCategories: FAQCategory[] = [
    {
      id: 'all',
      label: 'All Questions'
    },
    {
      id: 'therapy',
      label: 'Hormone Therapy'
    },
    {
      id: 'process',
      label: 'Our Process'
    },
    {
      id: 'safety',
      label: 'Safety & Efficacy'
    },
    {
      id: 'practical',
      label: 'Practical Information'
    }
  ];
  
  // Services data
  const hormoneServices: HormoneService[] = [
    {
      id: 1,
      title: "Bioidentical Estrogen Therapy",
      description: "Experience relief from menopausal symptoms with our precision-balanced estrogen therapy that mimics your body's natural hormones exactly, providing more effective symptom relief with fewer side effects.",
      icon: "venus",
      features: [
        "Relief from hot flashes and night sweats",
        "Improved sleep quality and duration",
        "Enhanced mood stability and mental clarity",
        "Protection against bone density loss",
        "Reduced vaginal dryness and discomfort"
      ],
      learnMoreLink: "/services/estrogen"
    },
    {
      id: 2,
      title: "Bioidentical Progesterone Therapy",
      description: "Restore hormonal balance with natural progesterone that works synergistically with estrogen, helping to relieve symptoms while providing protective benefits for your endometrium and overall well-being.",
      icon: "balance-scale",
      features: [
        "Balanced estrogen effects for optimal safety",
        "Improved sleep quality and relaxation",
        "Reduced anxiety and mood fluctuations",
        "Decreased risk of endometrial hyperplasia",
        "Relief from premenstrual and perimenopausal symptoms"
      ],
      learnMoreLink: "/services/progesterone"
    },
    {
      id: 3,
      title: "Women's Testosterone Therapy",
      description: "Reclaim your energy and drive with our precisely calibrated testosterone treatments specifically designed for women, addressing common symptoms of low testosterone while maintaining feminine characteristics.",
      icon: "fire",
      features: [
        "Increased energy levels and vitality",
        "Improved mental clarity, focus and memory",
        "Enhanced libido and sexual function",
        "Increased muscle tone and body composition",
        "Greater sense of well-being and confidence"
      ],
      learnMoreLink: "/services/testosterone"
    },
    {
      id: 4,
      title: "DHEA & Adrenal Support",
      description: "Revitalize your energy and stress resilience with our comprehensive DHEA and adrenal support therapies designed to combat fatigue, strengthen immunity, and restore youthful vitality.",
      icon: "bolt",
      features: [
        "Improved energy and reduced fatigue",
        "Enhanced immune system function",
        "Better stress response and adaptation",
        "Support for healthy aging processes",
        "Maintained bone and muscle integrity"
      ],
      learnMoreLink: "/services/dhea"
    },
    {
      id: 5,
      title: "Thyroid Optimization",
      description: "Restore your metabolic balance with our comprehensive thyroid treatment protocols that address the full spectrum of thyroid health, including T3, T4, and reverse T3 for optimal cellular energy.",
      icon: "heartbeat",
      features: [
        "Increased energy and reduced chronic fatigue",
        "Weight management support and metabolism enhancement",
        "Improved mental concentration and reduced brain fog",
        "Enhanced temperature regulation and comfort",
        "Healthier hair, skin, and nail quality"
      ],
      learnMoreLink: "/services/thyroid"
    },
    {
      id: 6,
      title: "Integrative Wellness Plans",
      description: "Experience truly individualized care with our comprehensive wellness plans that integrate hormone therapy with nutrition, exercise, stress management, and sleep optimization for profound transformation.",
      icon: "leaf",
      features: [
        "Comprehensive hormone and health assessment",
        "Personalized nutrition and supplement guidance",
        "Stress management and adrenal recovery techniques",
        "Sleep optimization protocols",
        "Regular monitoring and treatment adjustments"
      ],
      learnMoreLink: "/services/wellness"
    }
  ];
  
  // Process steps data
  const processSteps: ProcessStep[] = [
    {
      id: 1,
      title: "Comprehensive Assessment",
      description: "We begin with thorough testing and detailed evaluation of your hormonal profile, health history, symptoms, and wellness goals to create a complete picture of your unique needs."
    },
    {
      id: 2,
      title: "Personalized Treatment",
      description: "Based on your assessment results, we develop a customized bioidentical hormone therapy plan precisely calibrated to your body's specific requirements."
    },
    {
      id: 3,
      title: "Expert Implementation",
      description: "Your treatment begins with careful guidance from our specialists, who ensure you understand your protocol and know exactly what to expect throughout your journey."
    },
    {
      id: 4,
      title: "Continuous Optimization",
      description: "We regularly monitor your progress through follow-up testing and consultations, fine-tuning your treatment to achieve optimal results as your body responds."
    }
  ];
  
  // Filter FAQs by category
  const filteredFaqs = activeCategory === 'all' 
    ? faqItems 
    : faqItems.filter(item => item.category === activeCategory);
  
  // Handle scroll for navigation
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      
      if (servicesRef.current && scrollPosition < processRef.current?.offsetTop!) {
        setActiveTab('services');
      } else if (processRef.current && scrollPosition < faqRef.current?.offsetTop!) {
        setActiveTab('process');
      } else if (faqRef.current) {
        setActiveTab('faq');
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Toggle FAQ answer
  const toggleFaq = (id: number) => {
    if (activeFaq === id) {
      setActiveFaq(null);
    } else {
      setActiveFaq(id);
    }
  };
  
  // Scroll to section
  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveTab(sectionId);
  };

  // Handle booking navigation
  const handleConsultation = () => {
    navigate('/booking');
  };
  
  // Handle service detail navigation
  const handleServiceDetail = (url: string) => {
    navigate(url);
  };

  return (
    <div className="services-page">
      <SEO 
        title="Bioidentical Hormone Therapy Services | VitalBalanceHormones"
        description="Discover our personalized bioidentical hormone therapy services for women experiencing menopause, perimenopause, and hormonal imbalances. Restore your vitality with customized treatment plans."
        keywords="bioidentical hormone therapy, estrogen therapy, progesterone therapy, women's testosterone therapy, thyroid optimization, hormone treatment services, BHRT services"
        url="/services"
        schema={[servicesSchema, faqSchema]}
      />
      
      {/* Hero Section */}
      <section className="services-hero">
        <div className="hero-pattern"></div>
        <div className="hero-container">
          <div className="hero-badge">Premium Hormone Solutions</div>
          <h1 className="hero-title">Your Wellness Journey <span>Starts Here</span></h1>
          <p className="hero-description">
            Experience our comprehensive approach to women's hormone health with personalized bioidentical
            hormone treatments designed to address your unique symptoms and restore optimal balance.
          </p>
          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={handleConsultation}>
              <i className="icon-calendar-check btn-icon"></i>
              Schedule Your Consultation
            </button>
            <button className="btn btn-secondary" onClick={() => scrollToSection('services')}>
              <i className="icon-arrow-down btn-icon"></i>
              Explore Our Services
            </button>
          </div>
        </div>
      </section>
      
      {/* Services Navigation */}
      <nav className="services-nav">
        <div className="nav-container">
          <ul className="nav-tabs">
            {navTabs.map(tab => (
              <li 
                key={tab.id}
                className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => scrollToSection(tab.id)}
              >
                {tab.label}
              </li>
            ))}
          </ul>
        </div>
      </nav>
      
      {/* Main Content */}
      <main className="main-content">
        {/* Services Introduction */}
        <section id="services" ref={servicesRef} className="services-intro">
          <h2 className="section-title">Our Unique Approach</h2>
          <div className="section-subtitle">Hormone Therapy Solutions</div>
          <p className="section-description">
            At VitalBalanceHormones, we offer advanced bioidentical hormone therapies tailored to your unique biochemistry.
            Our science-based approach focuses on restoring optimal hormone levels to help you feel your best at every stage of life,
            addressing both symptoms and their root causes with precision and care.
          </p>
        </section>
        
        {/* Services Grid */}
        <div className="services-grid">
          {hormoneServices.map((service) => (
            <div key={service.id} className="service-card">
              <div className="service-header">
                <div className="service-icon">
                  <i className={`icon-${service.icon}`}></i>
                </div>
                <h3 className="service-title">{service.title}</h3>
              </div>
              <div className="service-body">
                <p className="service-description">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, index) => (
                    <li key={index} className="feature-item">{feature}</li>
                  ))}
                </ul>
                <a href={service.learnMoreLink} className="service-link" onClick={(e) => {
                  e.preventDefault();
                  handleServiceDetail(service.learnMoreLink);
                }}>
                  Learn more <i className="icon-arrow-right service-link-icon"></i>
                </a>
              </div>
            </div>
          ))}
        </div>
        
        {/* Process Section */}
        <section id="process" ref={processRef} className="process-section">
          <div className="process-pattern"></div>
          <div className="process-container">
            <div className="process-header">
              <h2 className="process-title">Our Therapeutic Approach</h2>
              <p className="process-description">
                We follow a thorough, personalized process to ensure you receive the most effective
                hormone therapy for your unique physiology and specific symptoms.
              </p>
            </div>
            <div className="process-steps">
              {processSteps.map((step) => (
                <div key={step.id} className="process-step">
                  <div className="step-number">{step.id}</div>
                  <div>
                    <h3 className="step-title">{step.title}</h3>
                    <p className="step-description">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Enhanced FAQ Section */}
        <section id="faq" ref={faqRef} className="faq-section">
          <div className="faq-pattern"></div>
          <div className="faq-container">
            <div className="faq-header">
              <h2 className="faq-title">Frequently Asked Questions</h2>
              <p className="faq-description">
                Find answers to common questions about bioidentical hormone therapy and our services.
              </p>
            </div>
            
            <div className="faq-categories">
              {faqCategories.map(category => (
                <div 
                  key={category.id}
                  className={`faq-category ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.label}
                </div>
              ))}
            </div>
            
            <div className="faq-list">
              {filteredFaqs.map((item) => (
                <div 
                  key={item.id} 
                  className={`faq-item ${activeFaq === item.id ? 'active' : ''}`}
                >
                  <div 
                    className="faq-question" 
                    onClick={() => toggleFaq(item.id)}
                  >
                    {item.question}
                    <i className="icon-chevron-down faq-icon"></i>
                  </div>
                  <div className="faq-answer">
                    <div className="faq-answer-content">
                      {item.answer}
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="faq-contact">
                <h3 className="faq-contact-title">Still Have Questions?</h3>
                <p className="faq-contact-text">
                  We're here to help. Schedule a consultation with our hormone specialists to discuss your specific concerns.
                </p>
                <button className="faq-contact-button" onClick={handleConsultation}>
                  <i className="icon-calendar-check faq-contact-icon"></i>
                  Schedule Your Consultation
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Services;